import { Switch, Route } from "wouter";
import { lazy, Suspense } from "react";
import DashboardLayout from "./components/DashboardLayout";
import LandingPage from "./pages/LandingPage";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const TeamOverview = lazy(() => import("./pages/TeamOverview"));
const Competitions = lazy(() => import("./pages/Competitions"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Training = lazy(() => import("./pages/Training"));
const TrainingModule = lazy(() => import("./pages/TrainingModule"));
const SetterTraining = lazy(() => import("./pages/SetterTraining"));
const Vision = lazy(() => import("./pages/Vision"));
const Playbook = lazy(() => import("./pages/Playbook"));
const PlaybookChapter = lazy(() => import("./pages/PlaybookChapter"));
const Coach = lazy(() => import("./pages/Coach"));
const BriefingArchive = lazy(() => import("./pages/BriefingArchive"));
const Roleplay = lazy(() => import("./pages/Roleplay"));
const RepProfile = lazy(() => import("./pages/RepProfile"));
const Admin = lazy(() => import("./pages/Admin"));
const Manager = lazy(() => import("./pages/Manager"));
const Feed = lazy(() => import("./pages/Feed"));
const BookingCheck = lazy(() => import("./pages/BookingCheck"));
const ReassignLeads = lazy(() => import("./pages/ReassignLeads"));
const TeamGoals = lazy(() => import("./pages/TeamGoals"));
const SyncHealth = lazy(() => import("./pages/SyncHealth"));
const Alerts = lazy(() => import("./pages/Alerts"));
const AdminPlaybook = lazy(() => import("./pages/AdminPlaybook"));
const MasterAdmin = lazy(() => import("./pages/MasterAdmin"));
const Proposal = lazy(() => import("./pages/Proposal"));
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));

function Loader() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
    </div>
  );
}

export default function App() {
  return (
    <Switch>
      {/* Public routes — no auth required */}
      <Route path="/" component={LandingPage} />
      <Route path="/login">
        <Suspense fallback={<Loader />}>
          <Login />
        </Suspense>
      </Route>
      <Route path="/proposals/:slug">
        {(params) => (
          <Suspense fallback={<Loader />}>
            <Proposal slug={params.slug!} />
          </Suspense>
        )}
      </Route>

      {/* Authenticated routes — wrapped in DashboardLayout (sidebar + nav) */}
      <Route>
        <DashboardLayout>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/leaderboard" component={Leaderboard} />
              <Route path="/team" component={TeamOverview} />
              <Route path="/competitions" component={Competitions} />
              <Route path="/announcements" component={Announcements} />
              <Route path="/setter-training" component={SetterTraining} />
              <Route path="/training" component={Training} />
              <Route path="/training/:slug">
                {(params) => <TrainingModule slug={params.slug!} />}
              </Route>
              <Route path="/vision" component={Vision} />
              <Route path="/playbook" component={Playbook} />
              <Route path="/playbook/:slug">
                {(params) => <PlaybookChapter slug={params.slug!} />}
              </Route>
              <Route path="/briefings" component={BriefingArchive} />
              <Route path="/coach">
                <Coach />
              </Route>
              <Route path="/coach/:threadId">
                {(params) => <Coach threadId={parseInt(params.threadId!, 10)} />}
              </Route>
              <Route path="/roleplay" component={Roleplay} />
              <Route path="/rep/:id">
                {(params) => <RepProfile repId={parseInt(params.id!, 10)} />}
              </Route>
              <Route path="/admin" component={Admin} />
              <Route path="/manager" component={Manager} />
              <Route path="/feed" component={Feed} />
              <Route path="/admin/booking-check" component={BookingCheck} />
              <Route path="/admin/reassign-leads" component={ReassignLeads} />
              <Route path="/admin/team-goals" component={TeamGoals} />
              <Route path="/admin/sync-health" component={SyncHealth} />
              <Route path="/admin/alerts" component={Alerts} />
              <Route path="/admin/playbook" component={AdminPlaybook} />
              <Route path="/master-admin" component={MasterAdmin} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </DashboardLayout>
      </Route>
    </Switch>
  );
}
